// Main-thread observability for the on-device scan pipeline. A singleton that
// (a) aggregates per-image stage timings the workers post back (decode / face /
// person / ocr / total) into rolling count, images/sec, and avg+p50+p95 per
// stage; (b) samples HOST LOAD — long-tasks (PerformanceObserver), event-loop
// lag, JS heap, and the device's core/memory counts; (c) emits a compact
// `[scan-metrics]` console summary while scanning; and (d) exposes start/stop +
// getSnapshot() for the live HUD.
//
// This is PURE OBSERVABILITY — it reads timings and host signals and never
// touches detection math, thresholds, or model outputs. It is gated by
// scanDebugEnabled(): when disabled, record calls short-circuit immediately and
// no observers/intervals are ever installed, so prod pays ~nothing.

// ---- Debug gate ---------------------------------------------------------
// Enabled on a staging host (hostname contains 'staging', or localhost), under
// the Vite dev/staging env flags, or per-tester via localStorage ph_scan_debug.
// Everything below only does real work when this returns true.
export function scanDebugEnabled(): boolean {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("ph_scan_debug") === "1") return true;
  } catch {
    /* localStorage can throw in sandboxed contexts */
  }
  try {
    if (typeof window !== "undefined" && window.location) {
      const h = window.location.hostname.toLowerCase();
      if (h.includes("staging") || h === "localhost" || h === "127.0.0.1") return true;
    }
  } catch {
    /* no window */
  }
  try {
    const env = import.meta.env as Record<string, unknown> | undefined;
    if (env && (env.DEV === true || env.VITE_SCAN_DEBUG === "1" || env.VITE_SCAN_DEBUG === "true")) return true;
  } catch {
    /* no import.meta.env */
  }
  return false;
}

const STAGES = ["decode", "face", "person", "ocr", "total"] as const;
export type ScanStage = (typeof STAGES)[number];

export interface StageStat {
  count: number;
  avg: number;
  p50: number;
  p95: number;
}

export interface ScanMetricsSnapshot {
  running: boolean;
  images: number;
  imagesPerSec: number;
  stages: Record<ScanStage, StageStat>;
  ocrCropsTotal: number;
  // Execution provider each model actually ran on (last observed).
  eps: { face: string | null; person: string | null; ocr: string | null };
  // Pool / thread configuration the controller reported.
  config: { poolSize: number; wasmThreads: number; crossOriginIsolated: boolean; backend: string };
  host: {
    longTaskCount: number;
    longTaskBlockingMs: number;
    loopLagMs: number;
    heapUsedMb: number | null;
    hardwareConcurrency: number | null;
    deviceMemory: number | null;
  };
  hostPressure: number;
}

// Timings a single scanned image contributes. All fields optional/guarded — a
// worker that couldn't time a stage just omits it.
export interface ScanTimingSample {
  decodeMs?: number;
  faceMs?: number;
  personMs?: number;
  ocrMs?: number;
  ocrCrops?: number;
  totalMs?: number;
  eps?: { face?: string | null; person?: string | null; ocr?: string | null };
}

const SAMPLE_CAP = 240; // bounded ring buffer per stage (percentiles cost ~nothing)
const IMG_WINDOW_MS = 10_000; // images/sec sliding window
const SUMMARY_MS = 2_000; // console summary cadence
const SUMMARY_EVERY_N = 25; // ...or every N images, whichever first
const LOOP_LAG_MS = 500; // event-loop lag sample interval

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((p / 100) * sorted.length)));
  return sorted[idx];
}

class ScanMetrics {
  private running = false;
  private images = 0;
  private ocrCropsTotal = 0;
  private samples: Record<ScanStage, number[]> = { decode: [], face: [], person: [], ocr: [], total: [] };
  private sums: Record<ScanStage, number> = { decode: 0, face: 0, person: 0, ocr: 0, total: 0 };
  private counts: Record<ScanStage, number> = { decode: 0, face: 0, person: 0, ocr: 0, total: 0 };
  private completedAt: number[] = []; // timestamps for images/sec window

  private eps = { face: null as string | null, person: null as string | null, ocr: null as string | null };
  private config = { poolSize: 0, wasmThreads: 1, crossOriginIsolated: false, backend: "wasm" };

  // Host load
  private longTaskCount = 0;
  private longTaskBlockingMs = 0;
  private loopLagMs = 0;
  private samplersOn = false; // host-load samplers installed (independent of debug)
  private ltObserver: PerformanceObserver | null = null;
  private loopTimer: ReturnType<typeof setInterval> | null = null;
  private summaryTimer: ReturnType<typeof setInterval> | null = null;
  private lastSummaryImages = 0;

  private now(): number {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
  }

  /** Install the host-load samplers (long-task observer + event-loop-lag timer).
   *  Idempotent. NOT debug-gated — the adaptive governor needs `hostPressure()`
   *  live in every environment, so this runs whenever scanning is active. Cost is
   *  one PerformanceObserver + one 500ms interval, torn down on pool shutdown. */
  private ensureSamplers(): void {
    if (this.samplersOn) return;
    this.samplersOn = true;

    // Long-task observer — count + total blocking time (TBT-style: the part of
    // each long task beyond the 50ms budget).
    try {
      if (typeof PerformanceObserver !== "undefined") {
        this.ltObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.longTaskCount += 1;
            this.longTaskBlockingMs += Math.max(0, entry.duration - 50);
          }
        });
        this.ltObserver.observe({ type: "longtask", buffered: true });
      }
    } catch {
      this.ltObserver = null; // longtask not supported (Safari/Firefox) — skip
    }

    // Event-loop lag: schedule a fixed interval and measure how late it actually
    // fires. A busy main thread makes the callback run late = lag.
    try {
      let expected = this.now() + LOOP_LAG_MS;
      this.loopTimer = setInterval(() => {
        const t = this.now();
        const lag = Math.max(0, t - expected);
        // EWMA so a single hiccup doesn't dominate the readout.
        this.loopLagMs = this.loopLagMs * 0.7 + lag * 0.3;
        expected = t + LOOP_LAG_MS;
      }, LOOP_LAG_MS);
    } catch {
      this.loopTimer = null;
    }
  }

  private teardownSamplers(): void {
    try { this.ltObserver?.disconnect(); } catch { /* already gone */ }
    this.ltObserver = null;
    if (this.loopTimer) { clearInterval(this.loopTimer); this.loopTimer = null; }
    this.samplersOn = false;
    this.longTaskCount = 0;
    this.longTaskBlockingMs = 0;
    this.loopLagMs = 0;
  }

  /** Host-load sampling ONLY (for the adaptive governor) — runs in all envs. */
  startSampling(): void { this.ensureSamplers(); }

  /** Stop host-load sampling (and the debug summary, if any). Called on pool shutdown. */
  stopSampling(): void {
    this.teardownSamplers();
    if (this.summaryTimer) { clearInterval(this.summaryTimer); this.summaryTimer = null; }
    this.running = false;
  }

  /** Begin the DEBUG aggregation + periodic console summary. No-op unless debug is
   *  enabled; also ensures the host-load samplers are installed. */
  start(): void {
    if (this.running || !scanDebugEnabled()) return;
    this.running = true;
    this.ensureSamplers();
    try {
      this.summaryTimer = setInterval(() => {
        if (this.images > this.lastSummaryImages) this.logSummary();
      }, SUMMARY_MS);
    } catch {
      this.summaryTimer = null;
    }
  }

  /** Stop everything (debug summary + samplers). */
  stop(): void {
    this.running = false;
    if (this.summaryTimer) { clearInterval(this.summaryTimer); this.summaryTimer = null; }
    this.teardownSamplers();
  }

  /** Record the pool/thread/backend config the controller resolved. */
  noteConfig(cfg: Partial<ScanMetrics["config"]>): void {
    if (!scanDebugEnabled()) return;
    this.config = { ...this.config, ...cfg };
  }

  private pushStage(stage: ScanStage, ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return;
    const buf = this.samples[stage];
    buf.push(ms);
    if (buf.length > SAMPLE_CAP) buf.shift();
    this.sums[stage] += ms;
    this.counts[stage] += 1;
  }

  /** (a) Ingest one scanned image's stage timings (posted by a worker via the
   *  controller). Cheap; guarded to never throw. No-op when debug disabled. */
  record(sample: ScanTimingSample): void {
    if (!scanDebugEnabled()) return;
    try {
      if (!this.running) this.start();
      this.images += 1;
      const t = this.now();
      this.completedAt.push(t);
      // Drop timestamps outside the images/sec window.
      const cutoff = t - IMG_WINDOW_MS;
      while (this.completedAt.length && this.completedAt[0] < cutoff) this.completedAt.shift();

      if (sample.decodeMs != null) this.pushStage("decode", sample.decodeMs);
      if (sample.faceMs != null) this.pushStage("face", sample.faceMs);
      if (sample.personMs != null) this.pushStage("person", sample.personMs);
      if (sample.ocrMs != null) this.pushStage("ocr", sample.ocrMs);
      if (sample.totalMs != null) this.pushStage("total", sample.totalMs);
      if (sample.ocrCrops) this.ocrCropsTotal += sample.ocrCrops;

      if (sample.eps) {
        if (sample.eps.face) this.eps.face = sample.eps.face;
        if (sample.eps.person) this.eps.person = sample.eps.person;
        if (sample.eps.ocr) this.eps.ocr = sample.eps.ocr;
      }

      if (this.images - this.lastSummaryImages >= SUMMARY_EVERY_N) this.logSummary();
    } catch {
      /* never let instrumentation break a scan */
    }
  }

  private stageStat(stage: ScanStage): StageStat {
    const sorted = [...this.samples[stage]].sort((a, b) => a - b);
    return {
      count: this.counts[stage],
      avg: this.counts[stage] ? Math.round(this.sums[stage] / this.counts[stage]) : 0,
      p50: Math.round(percentile(sorted, 50)),
      p95: Math.round(percentile(sorted, 95)),
    };
  }

  private imagesPerSec(): number {
    if (this.completedAt.length < 2) return 0;
    const span = (this.now() - this.completedAt[0]) / 1000;
    return span > 0 ? Math.round((this.completedAt.length / span) * 10) / 10 : 0;
  }

  private heapUsedMb(): number | null {
    try {
      const mem = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory;
      if (mem && typeof mem.usedJSHeapSize === "number") return Math.round(mem.usedJSHeapSize / (1024 * 1024));
    } catch {
      /* non-Chromium */
    }
    return null;
  }

  /** (d) Live snapshot for the HUD / console. */
  getSnapshot(): ScanMetricsSnapshot {
    const stages = {} as Record<ScanStage, StageStat>;
    for (const s of STAGES) stages[s] = this.stageStat(s);
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    return {
      running: this.running,
      images: this.images,
      imagesPerSec: this.imagesPerSec(),
      stages,
      ocrCropsTotal: this.ocrCropsTotal,
      eps: { ...this.eps },
      config: { ...this.config },
      host: {
        longTaskCount: this.longTaskCount,
        longTaskBlockingMs: Math.round(this.longTaskBlockingMs),
        loopLagMs: Math.round(this.loopLagMs),
        heapUsedMb: this.heapUsedMb(),
        hardwareConcurrency: nav?.hardwareConcurrency ?? null,
        deviceMemory: (nav as unknown as { deviceMemory?: number })?.deviceMemory ?? null,
      },
      hostPressure: this.hostPressure(),
    };
  }

  /**
   * (item 4) Normalized host pressure in [0,1], derived from event-loop lag and
   * long-task blocking rate. A parity-safe SIGNAL ONLY — nothing here changes
   * scheduling. The controller can read it to *optionally* back off pool /
   * coroutine count under load (see the TODO in scanController.js).
   */
  hostPressure(): number {
    // Loop lag: ~200ms sustained lag is "very busy".
    const lagP = Math.min(1, this.loopLagMs / 200);
    // Blocking rate over the images/sec window (ms blocked per second).
    const windowSec = Math.max(1, IMG_WINDOW_MS / 1000);
    const blockingPerSec = this.longTaskBlockingMs / windowSec;
    const ltP = Math.min(1, blockingPerSec / 1000); // ~1s blocked per sec == fully saturated
    return Math.max(lagP, ltP);
  }

  private logSummary(): void {
    this.lastSummaryImages = this.images;
    try {
      const s = this.getSnapshot();
      const fmt = (st: StageStat) => `${st.p50}/${st.p95}ms`;
      // eslint-disable-next-line no-console
      console.info(
        `[scan-metrics] ${s.images} imgs · ${s.imagesPerSec}/s · ` +
          `decode ${fmt(s.stages.decode)} face ${fmt(s.stages.face)} person ${fmt(s.stages.person)} ocr ${fmt(s.stages.ocr)} total ${fmt(s.stages.total)} (p50/p95) · ` +
          `EP face=${s.eps.face ?? "?"} person=${s.eps.person ?? "?"} ocr=${s.eps.ocr ?? "?"} · ` +
          `pool=${s.config.poolSize}×${s.config.wasmThreads}thr ${s.config.crossOriginIsolated ? "COI" : "no-COI"} ${s.config.backend} · ` +
          `heap=${s.host.heapUsedMb ?? "?"}MB longtasks=${s.host.longTaskCount}/${s.host.longTaskBlockingMs}ms lag=${s.host.loopLagMs}ms pressure=${s.hostPressure.toFixed(2)}`,
      );
    } catch {
      /* logging must never throw */
    }
  }
}

// Singleton — one metrics aggregator for the whole page.
export const scanMetrics = new ScanMetrics();

/** Ingest a worker timing sample (called by the controller per scanned file). */
export function recordScanMetrics(sample: ScanTimingSample): void {
  scanMetrics.record(sample);
}

/** Report the resolved pool/thread/backend config (called once by the controller). */
export function noteScanConfig(cfg: {
  poolSize?: number;
  wasmThreads?: number;
  crossOriginIsolated?: boolean;
  backend?: string;
}): void {
  scanMetrics.noteConfig(cfg);
}

/** Current host pressure in [0,1] (event-loop lag / long-task blocking). */
export function hostPressure(): number {
  return scanMetrics.hostPressure();
}

export function startScanMetrics(): void {
  scanMetrics.start();
}

export function stopScanMetrics(): void {
  scanMetrics.stop();
}

/** Install host-load samplers for the adaptive governor (runs in ALL envs; the
 *  controller calls this when scanning starts). Idempotent + cheap. */
export function startScanSampling(): void {
  scanMetrics.startSampling();
}

/** Tear down host-load sampling (controller calls this on pool shutdown). */
export function stopScanSampling(): void {
  scanMetrics.stopSampling();
}

export function getScanMetricsSnapshot(): ScanMetricsSnapshot {
  return scanMetrics.getSnapshot();
}

// Console hook for quick inspection at any time.
if (typeof window !== "undefined") {
  (window as unknown as { __phScanMetrics?: () => ScanMetricsSnapshot }).__phScanMetrics = () => scanMetrics.getSnapshot();
}
